import React, { useCallback, useEffect, useState } from 'react';
import { usePolling } from '@/hooks/usePolling';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Progress,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  ReloadOutlined,
  RocketOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { kickoffAnalysisApi, KickoffAnalysisDto } from '@/api/kickoffAnalysisApi';
import axiosClient from '@/api/axiosClient';

const { Text, Paragraph } = Typography;

interface Props {
  opportunityId: string;
}

const POLL_INTERVAL = 3000;

const readinessColor = (score: number | undefined): string => {
  if (score === undefined || score === null) return '#d9d9d9';
  if (score >= 70) return '#52c41a';
  if (score >= 40) return '#faad14';
  return '#ff4d4f';
};

const priorityColor = (priority?: string): string => {
  switch (priority?.toUpperCase()) {
    case 'HIGH': return 'red';
    case 'MEDIUM': return 'orange';
    case 'LOW': return 'green';
    default: return 'default';
  }
};

const KickoffAIPlanner: React.FC<Props> = ({ opportunityId }) => {
  const [analysis, setAnalysis] = useState<KickoffAnalysisDto | null>(null);
  const [allAnalyses, setAllAnalyses] = useState<KickoffAnalysisDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kickoffDateSet, setKickoffDateSet] = useState(false);
  const fetchLatest = useCallback(async () => {
    try {
      const res = await kickoffAnalysisApi.getLatest(opportunityId);
      if (res.status === 204) {
        setAnalysis(null);
        return null;
      }
      setAnalysis(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [opportunityId]);

  const fetchAllAnalyses = useCallback(async () => {
    try {
      const res = await kickoffAnalysisApi.getAll(opportunityId);
      setAllAnalyses(res.data ?? []);
    } catch {
      // ignore
    }
  }, [opportunityId]);

  const isRunning = analysis?.status === 'PENDING' || analysis?.status === 'RUNNING';

  // Poll while a plan is generating; pauses when the tab is hidden, stops on terminal.
  usePolling(
    async () => {
      const data = await fetchLatest();
      if (data && (data.status === 'COMPLETED' || data.status === 'FAILED')) {
        fetchAllAnalyses();
        return false;
      }
    },
    POLL_INTERVAL,
    isRunning,
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchLatest(),
      axiosClient.get(`/opportunities/${opportunityId}/plan`).catch(() => ({ data: null })),
      fetchAllAnalyses(),
    ]).then(([, planRes]) => {
      setLoading(false);
      setKickoffDateSet(Boolean(planRes?.data?.kickoffDate));
    });
  }, [opportunityId, fetchLatest, fetchAllAnalyses]);

  const handleTrigger = async () => {
    setTriggering(true);
    setError(null);
    try {
      const res = await kickoffAnalysisApi.trigger(opportunityId);
      setAnalysis(res.data);
      // Polling auto-starts when status is PENDING/RUNNING (see usePolling above).
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to start kickoff analysis';
      setError(msg);
    } finally {
      setTriggering(false);
    }
  };

  const isCompleted = analysis?.status === 'COMPLETED';
  const isFailed = analysis?.status === 'FAILED';

  const milestoneColumns = [
    {
      title: 'Milestone',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => <Text strong style={{ fontSize: 12 }}>{text}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => <Text style={{ fontSize: 12 }}>{text}</Text>,
    },
    {
      title: 'Target Date',
      dataIndex: 'targetDate',
      key: 'targetDate',
      width: 120,
      render: (text: string) => <Text style={{ fontSize: 12 }}>{text}</Text>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (text: string) => <Tag color={priorityColor(text)}>{text}</Tag>,
    },
  ];

  return (
    <Collapse
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'kickoff-ai',
          label: (
            <Space>
              <RocketOutlined style={{ color: 'var(--color-cyan)' }} />
              <Text strong style={{ fontSize: 13 }}>
                AI Kickoff Plan & Strategy
              </Text>
              {isCompleted && analysis?.readinessScore != null && (
                <Tag
                  color={readinessColor(analysis.readinessScore)}
                  icon={<CheckCircleOutlined />}
                >
                  Readiness: {analysis.readinessScore.toFixed(0)}%
                </Tag>
              )}
              {isRunning && <Tag color="processing">Generating Plan...</Tag>}
            </Space>
          ),
          children: (
            <div>
              {/* Action button */}
              <div style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={isCompleted || isFailed ? <ReloadOutlined /> : <ExperimentOutlined />}
                  loading={triggering || isRunning}
                  onClick={handleTrigger}
                  disabled={isRunning || !kickoffDateSet}
                  style={{ background: '#0891B2', borderColor: '#0891B2' }}
                >
                  {isRunning
                    ? 'Generating Plan...'
                    : isCompleted || isFailed
                      ? 'Re-generate Plan'
                      : 'Generate AI Kickoff Plan'}
                </Button>
                {!kickoffDateSet && !isCompleted && !isRunning && (
                  <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
                    Set a kickoff date above before generating the AI plan
                  </Text>
                )}
              </div>

              {error && (
                <Alert
                  type="error"
                  message={error}
                  closable
                  onClose={() => setError(null)}
                  style={{ marginBottom: 16 }}
                />
              )}

              {loading && !analysis && <Spin tip="Loading..." />}

              {isRunning && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">
                      AI is generating your kickoff plan and strategy...
                    </Text>
                  </div>
                </div>
              )}

              {isFailed && analysis?.errorMessage && (
                <Alert
                  type="error"
                  message="Plan Generation Failed"
                  description={analysis.errorMessage}
                  style={{ marginBottom: 16 }}
                />
              )}

              {isCompleted && analysis && (
                <>
                  {/* Readiness Score + Timeline */}
                  <Card
                    size="small"
                    style={{
                      marginBottom: 16,
                      background: 'var(--color-info-light, var(--color-bg-container))',
                      borderColor: 'var(--color-primary)',
                    }}
                  >
                    <Row align="middle" gutter={24}>
                      <Col>
                        <Progress
                          type="circle"
                          percent={Math.round(analysis.readinessScore ?? 0)}
                          size={80}
                          strokeColor={readinessColor(analysis.readinessScore)}
                          format={(pct) => (
                            <span style={{ fontSize: 16, fontWeight: 700 }}>{pct}%</span>
                          )}
                        />
                      </Col>
                      <Col flex={1}>
                        <div style={{ marginBottom: 4 }}>
                          <Text strong style={{ fontSize: 14 }}>Readiness Score</Text>
                        </div>
                        <Text type="secondary">
                          {analysis.readinessScore != null && analysis.readinessScore >= 70
                            ? 'Team is well-prepared to execute'
                            : analysis.readinessScore != null && analysis.readinessScore >= 40
                              ? 'Some preparation gaps to address'
                              : 'Significant preparation needed'}
                        </Text>
                      </Col>
                      <Col>
                        <Tag
                          icon={<CalendarOutlined />}
                          color="cyan"
                          style={{ fontSize: 14, padding: '4px 12px' }}
                        >
                          {analysis.timelineWeeks ?? '?'} weeks
                        </Tag>
                      </Col>
                    </Row>
                  </Card>

                  {/* Strategy Summary */}
                  {analysis.strategySummary && (
                    <Card size="small" title="Strategy Summary" style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                        {analysis.strategySummary.split('\n').map((line, i) => {
                          const trimmed = line.trim();
                          if (!trimmed) return <br key={i} />;
                          if (trimmed.startsWith('• ') || trimmed.startsWith('- ')) {
                            return (
                              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4, paddingLeft: 8 }}>
                                <span style={{ color: '#0891B2', fontWeight: 700, flexShrink: 0 }}>•</span>
                                <span>{trimmed.replace(/^[•\-]\s*/, '')}</span>
                              </div>
                            );
                          }
                          return <p key={i} style={{ margin: '0 0 8px 0' }}>{trimmed}</p>;
                        })}
                      </div>
                    </Card>
                  )}

                  {/* Kickoff Agenda */}
                  {analysis.kickoffAgenda && analysis.kickoffAgenda.length > 0 && (
                    <Card
                      size="small"
                      title={
                        <Space>
                          <RocketOutlined style={{ color: 'var(--color-cyan)' }} />
                          <span>Kickoff Meeting Agenda</span>
                        </Space>
                      }
                      style={{ marginBottom: 16, borderColor: '#a5f3fc' }}
                    >
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        {analysis.kickoffAgenda.map((item, i) => (
                          <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{item}</li>
                        ))}
                      </ol>
                    </Card>
                  )}

                  {/* Milestones Table */}
                  {analysis.milestones && analysis.milestones.length > 0 && (
                    <Card
                      size="small"
                      title={
                        <Space>
                          <CalendarOutlined style={{ color: 'var(--color-cyan)' }} />
                          <span>Key Milestones</span>
                        </Space>
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <Table
                        dataSource={analysis.milestones.map((m, i) => ({ ...m, key: i }))}
                        columns={milestoneColumns}
                        pagination={false}
                        size="small"
                      />
                    </Card>
                  )}

                  {/* Resource Recommendations */}
                  {analysis.resourceRecommendations && analysis.resourceRecommendations.length > 0 && (
                    <Card
                      size="small"
                      title={<><CheckCircleOutlined style={{ color: 'var(--color-primary)', marginRight: 8 }} />Resource Recommendations</>}
                      style={{ marginBottom: 16, background: 'var(--color-primary-light)', borderColor: '#91d5ff' }}
                    >
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        {analysis.resourceRecommendations.map((rec, i) => (
                          <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{rec}</li>
                        ))}
                      </ol>
                    </Card>
                  )}

                  {/* Risk Mitigation */}
                  {analysis.riskMitigation && analysis.riskMitigation.length > 0 && (
                    <Card
                      size="small"
                      title={<><WarningOutlined style={{ color: 'var(--color-warning)', marginRight: 8 }} />Risk Mitigation</>}
                      style={{ marginBottom: 16 }}
                    >
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        {analysis.riskMitigation.map((risk, i) => (
                          <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{risk}</li>
                        ))}
                      </ol>
                    </Card>
                  )}

                  {/* Key Deliverables */}
                  {analysis.keyDeliverables && analysis.keyDeliverables.length > 0 && (
                    <Card
                      size="small"
                      title={<><CheckCircleOutlined style={{ color: 'var(--color-success)', marginRight: 8 }} />Key Deliverables</>}
                      style={{ marginBottom: 16, background: 'var(--color-success-light)', borderColor: 'var(--color-success)' }}
                    >
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        {analysis.keyDeliverables.map((del, i) => (
                          <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{del}</li>
                        ))}
                      </ol>
                    </Card>
                  )}

                  {/* Detailed Plan */}
                  {analysis.detailedPlan && (
                    <Collapse
                      size="small"
                      style={{ marginBottom: 16 }}
                      items={[
                        {
                          key: 'detailed-plan',
                          label: <Text strong style={{ fontSize: 13 }}>Detailed Project Plan</Text>,
                          children: (
                            <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                              {analysis.detailedPlan.split('\n').map((line, i) => {
                                const trimmed = line.trim();
                                if (!trimmed) return <br key={i} />;
                                const boldMatch = trimmed.match(/^\*\*(.+?)\*\*/);
                                if (boldMatch) {
                                  const rest = trimmed.replace(/^\*\*.+?\*\*\s*/, '');
                                  return (
                                    <div key={i} style={{ marginTop: 12, marginBottom: 4 }}>
                                      <Text strong style={{ fontSize: 14, color: '#0e7490' }}>{boldMatch[1]}</Text>
                                      {rest && <span> {rest}</span>}
                                    </div>
                                  );
                                }
                                if (trimmed.startsWith('• ') || trimmed.startsWith('- ')) {
                                  return (
                                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3, paddingLeft: 12 }}>
                                      <span style={{ color: '#0891B2', fontWeight: 700, flexShrink: 0 }}>•</span>
                                      <span>{trimmed.replace(/^[•\-]\s*/, '')}</span>
                                    </div>
                                  );
                                }
                                return <p key={i} style={{ margin: '0 0 6px 0' }}>{trimmed}</p>;
                              })}
                            </div>
                          ),
                        },
                      ]}
                    />
                  )}

                  {/* Metadata */}
                  <div style={{ textAlign: 'right' }}>
                    <Tag color="cyan" style={{ marginRight: 8 }}>Saved to Content Library</Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {analysis.completedAt &&
                        `Completed: ${new Date(analysis.completedAt).toLocaleString()}`}
                      {analysis.modelUsed && ` · Model: ${analysis.modelUsed}`}
                      {analysis.tokensUsed != null &&
                        ` · Tokens: ${analysis.tokensUsed.toLocaleString()}`}
                      {analysis.triggeredByName && ` · by ${analysis.triggeredByName}`}
                    </Text>
                  </div>
                </>
              )}

              {/* Analysis History */}
              {allAnalyses.length > 1 && (
                <Collapse
                  size="small"
                  style={{ marginTop: 16 }}
                  items={[
                    {
                      key: 'history',
                      label: (
                        <Space>
                          <HistoryOutlined />
                          <Text style={{ fontSize: 12 }}>
                            Previous Analyses ({allAnalyses.length - 1})
                          </Text>
                        </Space>
                      ),
                      children: (
                        <div>
                          {allAnalyses.slice(1).map((prev) => (
                            <Card
                              key={prev.id}
                              size="small"
                              style={{ marginBottom: 8 }}
                            >
                              <Row align="middle" gutter={12}>
                                <Col>
                                  <Tag color={readinessColor(prev.readinessScore)}>
                                    Readiness: {prev.readinessScore?.toFixed(0)}%
                                  </Tag>
                                </Col>
                                <Col flex={1}>
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    Timeline: {prev.timelineWeeks} weeks
                                    {prev.completedAt &&
                                      ` · ${new Date(prev.completedAt).toLocaleString()}`}
                                    {prev.triggeredByName &&
                                      ` · by ${prev.triggeredByName}`}
                                  </Text>
                                </Col>
                              </Row>
                              {prev.strategySummary && (
                                <Paragraph
                                  style={{ margin: '8px 0 0', fontSize: 12 }}
                                  ellipsis={{ rows: 2, expandable: true }}
                                >
                                  {prev.strategySummary}
                                </Paragraph>
                              )}
                            </Card>
                          ))}
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

export default KickoffAIPlanner;
